/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, OnDestroy, OnInit, signal, ViewEncapsulation} from '@angular/core';
import {UIRouterModule} from '@uirouter/angular';
import {BsDropdownModule} from 'ngx-bootstrap/dropdown';
import {AppEventBusService} from '../../common/events/app-event-bus.service';

import template from './session-info.component.html';
import {AUTH_EVENT} from '../../../components/auth/auth-constants';
import {SESSION_EVENT} from '../../common/constants/misc-constants';
import {LocalDebugInfo} from '../../../components/session/local-debug-info';
import {gravatarHash} from '../../../components/util/gravatar-hash';
import {AppShellStateService} from '../app-shell-state.service';
import {AdnFormStateService} from '../../../components/util/adn-form-state.service';
import {AuthService} from '../../../components/auth/auth.service';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {WorkspaceRegistryService} from '../../../components/workspace-registry.service';
import {DocumentShellService} from '../document-shell.service';
import {StateService} from '@uirouter/core';
import {APP_SHARED} from '../../common/app-shared';

@Component({
  standalone: true,
  imports: [...APP_SHARED, UIRouterModule, BsDropdownModule],
  selector: 'adn-session-info',
  template,
  encapsulation: ViewEncapsulation.None
})
export class SessionInfoComponent implements OnInit, OnDestroy {
  private readonly userState = signal<any>(null);
  private destroyUserProfileUpdateListener?: () => void;

  get user(): any { return this.userState(); }
  set user(value: any) { this.userState.set(value); }
  profile: any;

  constructor(
    readonly shell: AppShellStateService,
    private readonly eventBus: AppEventBusService,
    private readonly $state: StateService,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    @Inject(AuthService) private readonly AuthService: any,
    @Inject(WorkspaceRegistryService) private readonly WorkspaceRegistry: any,
    @Inject(AdnFormStateService) private readonly adnFormState: any,
    private readonly documentShell: DocumentShellService
  ) {
  }

  ngOnInit(): void {
    this.user = this.localUserProfile.get();
    if (!this.user) {
      return;
    }
    this.profile = this.user.profile;
    this.WorkspaceRegistry.getPromise();
    this.destroyUserProfileUpdateListener = this.eventBus.on(SESSION_EVENT.userProfileUpdate, () => {
      this.user = {...this.localUserProfile.get()};
      this.profile = this.user.profile;
    });
  }

  ngOnDestroy(): void {
    this.destroyUserProfileUpdateListener?.();
  }

  get appNetworks(): any[] {
    return this.shell.appNetworks;
  }

  get hasNetworks(): boolean {
    return this.appNetworks.length > 0;
  }

  get hasMultipleNetworks(): boolean {
    return this.appNetworks.length > 1;
  }

  get gravatarUrl(): string {
    const hash = gravatarHash(this.user?.username);
    const size = this.shell.gravatarSize || 22;
    return `https://secure.gravatar.com/avatar/${hash}?s=${size}&d=identicon`;
  }

  get displayName(): string {
    return this.user?.displayName || this.user?.name || '';
  }

  switchApp(app: string): void {
    this.shell.switchApp(app);
  }

  signOut(): void {
    // Drop dirty-form tracking first so the confirm-nav hook cannot race Sign Out.
    this.adnFormState?.deregister();
    this.documentShell.setBusy(true);
    this.AuthService.signOut();
    LocalDebugInfo.obtain().setInfo('expired', new Date());
    const nav = this.$state.go('sign-in', {explicitlySignedOut: true});
    const clearBusy = () => this.documentShell.setBusy(false);
    if (nav && typeof nav.then === 'function') {
      nav.then(clearBusy, clearBusy);
    }
    this.eventBus.emit(AUTH_EVENT.logoutSuccess);
  }
}
